(() => {
  const ROOT_SELECTOR = '[data-sf-home-hero-media]';

  if (window.SplitflaskHomeHeroVideo) {
    window.SplitflaskHomeHeroVideo.init(document);
    return;
  }

  const controllers = new WeakMap();

  const addMediaListener = (query, listener) => {
    if (query.addEventListener) query.addEventListener('change', listener);
    else query.addListener(listener);
  };

  const removeMediaListener = (query, listener) => {
    if (query.removeEventListener) query.removeEventListener('change', listener);
    else query.removeListener(listener);
  };

  class HomeHeroVideoController {
    constructor(root) {
      this.root = root;
      this.videos = [...root.querySelectorAll('.sf-home-hero__video')];
      this.toggleButton = root.querySelector('[data-sf-home-hero-video-toggle]');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.inView = true;
      this.playbackRequested = !this.reducedMotionQuery.matches;
      this.hasPlaybackInteraction = false;
      this.controlReady = false;
      this.startupReady = false;
      this.destroyed = false;
      this.sync = this.sync.bind(this);
      this.togglePlayback = this.togglePlayback.bind(this);
      this.handlePlaying = this.handlePlaying.bind(this);
      this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);

      addMediaListener(this.mobileQuery, this.sync);
      addMediaListener(this.reducedMotionQuery, this.handleReducedMotionChange);
      document.addEventListener('visibilitychange', this.sync);
      this.toggleButton?.addEventListener('click', this.togglePlayback);
      this.videos.forEach((video) => video.addEventListener('playing', this.handlePlaying));

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.inView = entry.isIntersecting;
            this.sync();
          },
          { threshold: 0.05 }
        );
        this.observer.observe(root);
      }

      // The high-priority poster renders first; video must not compete with
      // the initial image, styles, or purchase/navigation scripts.
      this.allowStartup = () => {
        const start = () => {
          if (this.destroyed) return;
          this.startupReady = true;
          this.sync();
        };
        if ('requestIdleCallback' in window) {
          this.startupIdle = window.requestIdleCallback(start, { timeout: 1500 });
        } else {
          this.startupTimer = window.setTimeout(start, 250);
        }
      };
      if (document.readyState === 'complete') this.allowStartup();
      else window.addEventListener('load', this.allowStartup, { once: true });
      this.sync();
    }

    getLayer(video) {
      return video.closest('.sf-home-hero__video-layer');
    }

    getActiveVideo() {
      const preferredClass = this.mobileQuery.matches
        ? 'sf-home-hero__video-layer--mobile'
        : 'sf-home-hero__video-layer--desktop';

      return this.videos.find((video) => this.getLayer(video)?.classList.contains(preferredClass)) || null;
    }

    isActiveForViewport(video) {
      return video === this.getActiveVideo();
    }

    getSources(video) {
      const sourceData = this.getLayer(video)?.querySelector('[data-sf-home-hero-video-sources]');
      if (!sourceData) return [];

      try {
        const sources = JSON.parse(sourceData.textContent || '[]');
        return Array.isArray(sources) ? sources : [];
      } catch (error) {
        return [];
      }
    }

    getBestSource(video) {
      const sources = this.getSources(video);
      const mp4Sources = sources.filter((source) => {
        const format = String(source.format || '').toLowerCase();
        const mimeType = String(source.mime_type || source.mimeType || '').toLowerCase();
        return format === 'mp4' || mimeType.includes('mp4') || String(source.url || '').includes('.mp4');
      });
      const candidates = (mp4Sources.length ? mp4Sources : sources)
        .filter((source) => source.url)
        .sort((a, b) => Number(a.height || 0) - Number(b.height || 0));

      if (!candidates.length) return null;

      const targetHeight = this.mobileQuery.matches ? 720 : 1080;
      return candidates.find((source) => Number(source.height || 0) >= targetHeight) || candidates[candidates.length - 1];
    }

    loadVideo(video) {
      if (video.dataset.sfSourceLoaded === 'true') return true;

      const source = this.getBestSource(video);
      if (!source) return false;

      video.src = source.url;
      video.dataset.sfSourceLoaded = 'true';
      video.load();
      return true;
    }

    updateButton() {
      if (!this.toggleButton) return;

      this.toggleButton.setAttribute('aria-pressed', String(this.playbackRequested));
      this.toggleButton.setAttribute('aria-label', this.playbackRequested ? 'Pause product video' : 'Play product video');
      this.toggleButton.hidden = !this.controlReady || this.reducedMotionQuery.matches;
    }

    togglePlayback() {
      this.hasPlaybackInteraction = true;
      this.playbackRequested = !this.playbackRequested;
      this.controlReady = true;
      this.updateButton();
      this.sync();
    }

    handleReducedMotionChange() {
      if (!this.hasPlaybackInteraction) {
        this.playbackRequested = !this.reducedMotionQuery.matches;
      }
      this.sync();
    }

    shouldPlay(video) {
      return (
        this.startupReady &&
        this.playbackRequested &&
        !this.reducedMotionQuery.matches &&
        document.visibilityState !== 'hidden' &&
        this.inView &&
        this.isActiveForViewport(video)
      );
    }

    handlePlaying(event) {
      const video = event.currentTarget;
      if (!this.shouldPlay(video)) return;

      this.getLayer(video)?.classList.add('is-playing');
      this.controlReady = true;
      this.updateButton();
    }

    handlePlaybackFailure(video) {
      if (!this.isActiveForViewport(video)) return;

      this.playbackRequested = false;
      this.controlReady = true;
      this.getLayer(video)?.classList.remove('is-playing');
      this.updateButton();
    }

    sync() {
      const motionAllowed = !this.reducedMotionQuery.matches;
      const activeVideo = this.getActiveVideo();

      this.videos.forEach((video) => {
        const layer = this.getLayer(video);
        const isActiveVideo = video === activeVideo;
        const shouldPlay = this.shouldPlay(video);

        layer?.classList.toggle('is-active', isActiveVideo);

        if (!shouldPlay) {
          video.pause();
          if (!motionAllowed || !isActiveVideo) layer?.classList.remove('is-playing');
          return;
        }

        if (!this.loadVideo(video)) {
          this.handlePlaybackFailure(video);
          return;
        }

        video.autoplay = true;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        const playPromise = video.play();
        if (playPromise) {
          playPromise
            .catch(() => {
              if (this.shouldPlay(video)) this.handlePlaybackFailure(video);
            });
        }
      });

      this.updateButton();
    }

    destroy() {
      this.destroyed = true;
      window.removeEventListener('load', this.allowStartup);
      if (this.startupIdle !== undefined) window.cancelIdleCallback(this.startupIdle);
      window.clearTimeout(this.startupTimer);
      removeMediaListener(this.mobileQuery, this.sync);
      removeMediaListener(this.reducedMotionQuery, this.handleReducedMotionChange);
      document.removeEventListener('visibilitychange', this.sync);
      this.toggleButton?.removeEventListener('click', this.togglePlayback);
      if (this.observer) this.observer.disconnect();
      this.videos.forEach((video) => {
        video.removeEventListener('playing', this.handlePlaying);
        video.pause();
        video.removeAttribute('src');
        video.load();
      });
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (!controllers.has(root) && root.querySelector('.sf-home-hero__video')) {
        controllers.set(root, new HomeHeroVideoController(root));
      }
    });
  };

  window.SplitflaskHomeHeroVideo = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector(ROOT_SELECTOR);
    const controller = root && controllers.get(root);
    if (controller) {
      controller.destroy();
      controllers.delete(root);
    }
  });
})();
